/**
 * Contact + location blocks — extracted from ActionBlockRenderer.jsx (v119).
 *
 * Includes: normalisePhone (helper), WhatsAppBlock, AppDownloadBlock,
 * AppointmentBlock, EnquiryBlock, LocationBlock. Re-exported from
 * ActionBlockRenderer.jsx.
 */
import { useState, useEffect, useRef } from "react";
import { Play, Send, MessageCircle, Calendar, MapPin, Mail, Navigation } from "lucide-react";
import { getCurrentPositionSafe, isGeolocationSupported } from "../../lib/geolocation";
import PhoneInput from "../PhoneInput";
import MarketingConsentCheckbox from "../MarketingConsentCheckbox";
import { _postPublicLead } from "./_shared";
// v145.26 — Read the signed-in user so contact / appointment / enquiry
// blocks can auto-fill name / email / phone. Falls back to blank
// fields for anonymous visitors — behaviour unchanged there.
import { useAuth } from "../../context/AuthContext";
import { openExternalUrl } from "../../lib/openExternal";

function normalisePhone(raw) {
  return (raw || "").replace(/[^\d]/g, "");
}

export function WhatsAppBlock({ block, onClick, editable }) {
  const c = block.config || {};
  const phone = normalisePhone(c.phone);
  const prefill = c.prefill ? `?text=${encodeURIComponent(c.prefill)}` : "";
  const href = phone ? `https://wa.me/${phone}${prefill}` : "#";
  return (
    <a
      href={href}
      target={editable ? undefined : "_blank"}
      rel="noreferrer"
      onClick={(e) => onClick(e, href)}
      className="ah-block flex items-center gap-3"
      style={{
        background: "#25D366", color: "#fff",
        borderColor: "var(--ah-fg)",
      }}
      data-testid={`block-whatsapp-${block.block_id}`}
    >
      {/* Inline WhatsApp glyph (official-shape, single color) */}
      <svg viewBox="0 0 24 24" width="26" height="26" fill="#fff" aria-hidden="true" className="flex-shrink-0">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.371-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347zM12.005 21.785h-.004a9.87 9.87 0 01-5.032-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.889-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.005 0C5.463 0 .141 5.322.138 11.865c0 2.096.547 4.142 1.588 5.945L.057 24l6.335-1.652a11.882 11.882 0 005.607 1.427h.005c6.542 0 11.865-5.322 11.868-11.865a11.821 11.821 0 00-3.462-8.352z" />
      </svg>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm truncate">{c.label || "Chat on WhatsApp"}</p>
        {phone ? (
          <p className="text-xs opacity-90 font-mono">+{phone}</p>
        ) : (
          <p className="text-xs opacity-80">Add a WhatsApp number in the editor</p>
        )}
      </div>
      <MessageCircle className="w-4 h-4 flex-shrink-0" strokeWidth={2.5} />
    </a>
  );
}

// v146.90 — Telegram CTA sits alongside WhatsApp in the Messaging group.
// Supports either a @username (opens https://t.me/<username>) or a phone
// number (opens https://t.me/+<E.164>). Editors can also paste a full URL.
function telegramHref(input) {
  const raw = String(input || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const cleaned = raw.replace(/^@+/, "");
  // Phone-style (all digits or leading +) → t.me/+<digits>
  const digits = cleaned.replace(/\D/g, "");
  if (/^\+?\d[\d\s-]{5,}$/.test(cleaned) && digits.length >= 6) {
    return `https://t.me/+${digits}`;
  }
  return `https://t.me/${cleaned}`;
}

export function TelegramBlock({ block, onClick, editable }) {
  const c = block.config || {};
  const handleOrPhone = (c.handle || c.phone || "").trim();
  const href = telegramHref(handleOrPhone);
  const displayLabel = handleOrPhone
    ? (handleOrPhone.startsWith("@") ? handleOrPhone : (
        /^\+?\d[\d\s-]{5,}$/.test(handleOrPhone) ? `+${handleOrPhone.replace(/\D/g, "")}` : `@${handleOrPhone.replace(/^@+/, "")}`
      ))
    : "";
  return (
    <a
      href={href || "#"}
      target={editable ? undefined : "_blank"}
      rel="noreferrer"
      onClick={(e) => onClick(e, href)}
      className="ah-block flex items-center gap-3"
      style={{
        background: "#229ED9", color: "#fff",
        borderColor: "var(--ah-fg)",
      }}
      data-testid={`block-telegram-${block.block_id}`}
    >
      {/* Inline Telegram paper-plane glyph — single lead icon (no trailing
          plane) so the card doesn't render two identical marks. */}
      <svg viewBox="0 0 496 512" width="26" height="26" fill="#fff" aria-hidden="true" className="flex-shrink-0">
        <path d="M248 8C111 8 0 119 0 256s111 248 248 248 248-111 248-248S385 8 248 8zm121.8 169.9l-40.7 191.8c-3 13.6-11.1 16.9-22.4 10.5l-62-45.7-29.9 28.8c-3.3 3.3-6.1 6.1-12.5 6.1l4.4-63.1 114.9-103.8c5-4.4-1.1-6.9-7.7-2.5l-142 89.4-61.2-19.1c-13.3-4.2-13.6-13.3 2.8-19.7l239.1-92.2c11.1-4 20.8 2.7 17.2 19.5z" />
      </svg>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm truncate">{c.label || "Chat on Telegram"}</p>
        {displayLabel ? (
          // Higher-contrast handle line — white/95 on Telegram blue reads
          // as body text, not disabled state.
          <p className="text-xs text-white/95 font-mono truncate">{displayLabel}</p>
        ) : (
          <p className="text-xs text-white/85">Add a Telegram @username or phone in the editor</p>
        )}
      </div>
    </a>
  );
}

export function AppDownloadBlock({ block, onClick, editable }) {
  const c = block.config || {};
  const layout = c.layout === "stack" ? "flex-col" : "flex-row";
  const hasIos = !!c.ios_url;
  const hasAndroid = !!c.android_url;
  return (
    <div className="space-y-2" data-testid={`block-app-download-${block.block_id}`}>
      {c.headline && <p className="ah-title text-center text-lg">{c.headline}</p>}
      {c.subtext && <p className="text-xs ah-muted text-center">{c.subtext}</p>}
      <div className={`flex ${layout} gap-2 sm:gap-3 justify-center flex-wrap`}>
        {hasIos && (
          <a
            href={c.ios_url}
            target={editable ? undefined : "_blank"}
            rel="noreferrer"
            onClick={(e) => onClick(e, c.ios_url)}
            className="inline-flex items-center gap-2 px-4 py-2.5"
            style={{
              background: "#0a0a0a", color: "#fff",
              border: "2px solid var(--ah-fg)",
              borderRadius: "10px",
              minWidth: "170px",
            }}
            data-testid={`block-app-download-ios-${block.block_id}`}
          >
            {/* Official Apple silhouette */}
            <svg viewBox="0 0 24 24" width="28" height="28" fill="#fff" aria-hidden="true" className="flex-shrink-0">
              <path d="M17.05 12.04c-.02-2.29 1.87-3.39 1.96-3.44-1.07-1.57-2.74-1.78-3.33-1.8-1.42-.14-2.77.83-3.5.83-.72 0-1.83-.81-3.02-.79-1.55.02-2.99.9-3.79 2.29-1.62 2.81-.41 6.96 1.15 9.24.77 1.11 1.68 2.36 2.87 2.32 1.16-.05 1.6-.75 3-.75s1.79.75 3.01.72c1.25-.02 2.03-1.13 2.79-2.25.88-1.29 1.24-2.54 1.26-2.6-.03-.01-2.41-.93-2.4-3.77zM14.66 5.31c.64-.78 1.07-1.86.95-2.94-.92.04-2.04.61-2.7 1.39-.59.69-1.11 1.79-.97 2.85 1.03.08 2.08-.52 2.72-1.3z" />
            </svg>
            <div className="text-left leading-tight">
              <p className="text-[10px] opacity-80">Download on the</p>
              <p className="font-bold text-base -mt-0.5">App Store</p>
            </div>
          </a>
        )}
        {hasAndroid && (
          <a
            href={c.android_url}
            target={editable ? undefined : "_blank"}
            rel="noreferrer"
            onClick={(e) => onClick(e, c.android_url)}
            className="inline-flex items-center gap-2 px-4 py-2.5"
            style={{
              background: "#0a0a0a", color: "#fff",
              border: "2px solid var(--ah-fg)",
              borderRadius: "10px",
              minWidth: "170px",
            }}
            data-testid={`block-app-download-android-${block.block_id}`}
          >
            {/* Official Google Play triangular play icon (4 colors) */}
            <svg viewBox="0 0 512 512" width="28" height="28" aria-hidden="true" className="flex-shrink-0">
              <path fill="#00d3ff" d="M40 20c-7 4-11 12-11 22v420c0 10 4 18 11 22l229-229z" />
              <path fill="#00f076" d="M40 20l229 235L363 161 65 5c-11-6-19-4-25 15z" />
              <path fill="#ffd400" d="M363 161L269 255l94 94 96-55c14-8 20-19 20-32-2-12-8-24-24-34z" />
              <path fill="#f8353b" d="M40 490l323-141-94-94L40 484z" />
            </svg>
            <div className="text-left leading-tight">
              <p className="text-[10px] opacity-80">GET IT ON</p>
              <p className="font-bold text-base -mt-0.5">Google Play</p>
            </div>
          </a>
        )}
        {!hasIos && !hasAndroid && (
          <p className="text-xs ah-muted text-center">Add an App Store or Google Play URL in the editor.</p>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Phase 7 blocks — Appointment / Enquiry / Location
// ---------------------------------------------------------------------------

export function AppointmentBlock({ block, editable }) {
  const c = block.config || {};
  const { user } = useAuth();
  const [state, setState] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    appointment_date: "",
    appointment_slot: "",
    message: "",
    // v146.44 — WhatsApp broadcast opt-in, default ON.
    marketing_consent: true,
  });
  const [status, setStatus] = useState("idle");
  const slots = Array.isArray(c.slots) && c.slots.length ? c.slots : ["10:00 AM", "12:00 PM", "3:00 PM", "6:00 PM"];
  // v145.26 — Backfill blank name/email/phone when auth resolves.
  useEffect(() => {
    if (!user) return;
    setState((prev) => ({
      ...prev,
      name: prev.name || user.name || "",
      email: prev.email || user.email || "",
      phone: prev.phone || user.phone || "",
    }));
  }, [user?.user_id]);
  const submit = async (e) => {
    e.preventDefault();
    if (editable) return;
    setStatus("submitting");
    try {
      await _postPublicLead(block, {
        intent: "appointment",
        name: state.name,
        email: state.email,
        phone: state.phone,
        message: state.message,
        appointment_date: state.appointment_date,
        appointment_slot: state.appointment_slot,
        marketing_consent: state.marketing_consent,
      });
      setStatus("ok");
      setState({ name: "", email: "", phone: "", appointment_date: "", appointment_slot: "", message: "", marketing_consent: true });
    } catch {
      setStatus("err");
    }
  };
  return (
    <form onSubmit={submit} className="ah-block space-y-2" data-testid={`block-appointment-${block.block_id}`}>
      <div className="flex items-center gap-2 justify-center">
        <Calendar className="w-4 h-4 ah-accent" strokeWidth={2.5} />
        <p className="font-bold text-sm">{c.headline || "Book an appointment"}</p>
      </div>
      {c.subtext && <p className="text-xs ah-muted text-center">{c.subtext}</p>}
      <input
        type="text" placeholder="Your name" required
        value={state.name} onChange={(e) => setState({ ...state, name: e.target.value })}
        className="w-full border-2 px-2 py-1 text-sm bg-transparent" style={{ borderColor: "var(--ah-fg)" }}
        data-testid={`appointment-input-name-${block.block_id}`}
      />
      <input
        type="email" placeholder="you@example.com" required
        value={state.email} onChange={(e) => setState({ ...state, email: e.target.value })}
        className="w-full border-2 px-2 py-1 text-sm bg-transparent" style={{ borderColor: "var(--ah-fg)" }}
        data-testid={`appointment-input-email-${block.block_id}`}
      />
      <PhoneInput
        value={state.phone}
        onChange={(v) => setState({ ...state, phone: v })}
        required={!!c.phone_required}
        placeholder={c.phone_required ? "10-digit mobile number (required)" : "10-digit mobile number (optional)"}
        data-testid={`appointment-input-phone-${block.block_id}`}
        className="w-full"
      />
      <MarketingConsentCheckbox
        checked={state.marketing_consent}
        onChange={(v) => setState({ ...state, marketing_consent: v })}
        testid={`appointment-consent-${block.block_id}`}
        compact
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider block mb-1 opacity-70">Date</label>
          <input
            type="date" required
            value={state.appointment_date} onChange={(e) => setState({ ...state, appointment_date: e.target.value })}
            className="w-full border-2 px-2 py-2 text-sm bg-transparent min-h-[40px]"
            style={{ borderColor: "var(--ah-fg)", WebkitAppearance: "none" }}
            data-testid={`appointment-input-date-${block.block_id}`}
          />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider block mb-1 opacity-70">Time slot</label>
          <select
            value={state.appointment_slot} onChange={(e) => setState({ ...state, appointment_slot: e.target.value })}
            className="w-full border-2 px-2 py-2 text-sm bg-transparent min-h-[40px]"
            style={{ borderColor: "var(--ah-fg)" }}
            data-testid={`appointment-input-slot-${block.block_id}`}
          >
            <option value="">Pick a time…</option>
            {slots.map((s, i) => (
              <option key={i} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>
      <textarea
        placeholder="Notes (optional)" rows={2}
        value={state.message} onChange={(e) => setState({ ...state, message: e.target.value })}
        className="w-full border-2 px-2 py-1 text-sm bg-transparent" style={{ borderColor: "var(--ah-fg)" }}
        data-testid={`appointment-input-notes-${block.block_id}`}
      />
      <button
        type="submit"
        disabled={status === "submitting" || editable}
        className="w-full border-2 font-bold text-sm py-1.5 flex items-center justify-center gap-2 disabled:opacity-50"
        style={{ borderColor: "var(--ah-fg)", background: "var(--ah-cta-bg)", color: "var(--ah-cta-fg)" }}
        data-testid={`appointment-submit-${block.block_id}`}
      >
        <Calendar className="w-3 h-3" /> {c.button_label || "Request appointment"}
      </button>
      {status === "ok" && <p className="text-xs font-bold text-center ah-accent" data-testid={`appointment-thanks-${block.block_id}`}>Thanks! We&apos;ll confirm your slot soon.</p>}
      {status === "err" && <p className="text-xs font-bold text-center" style={{ color: "#dc2626" }}>Something went wrong. Please try again.</p>}
    </form>
  );
}

export function EnquiryBlock({ block, editable }) {
  const c = block.config || {};
  const { user } = useAuth();
  const [state, setState] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    subject: "",
    message: "",
    // v146.44 — WhatsApp broadcast opt-in, default ON.
    marketing_consent: true,
  });
  const [status, setStatus] = useState("idle");
  // v145.26 — Backfill when auth resolves after mount (session fetch
  // races the block render). Only overwrites still-blank fields.
  useEffect(() => {
    if (!user) return;
    setState((prev) => ({
      name: prev.name || user.name || "",
      email: prev.email || user.email || "",
      phone: prev.phone || user.phone || "",
      subject: prev.subject,
      message: prev.message,
    }));
  }, [user?.user_id]);
  const submit = async (e) => {
    e.preventDefault();
    if (editable) return;
    setStatus("submitting");
    try {
      await _postPublicLead(block, {
        intent: "enquiry",
        name: state.name,
        email: state.email,
        phone: state.phone,
        subject: state.subject,
        message: state.message,
        marketing_consent: state.marketing_consent,
      });
      setStatus("ok");
      setState({ name: "", email: "", phone: "", subject: "", message: "", marketing_consent: true });
    } catch {
      setStatus("err");
    }
  };
  return (
    <form onSubmit={submit} className="ah-block space-y-2" data-testid={`block-enquiry-${block.block_id}`}>
      <div className="flex items-center gap-2 justify-center">
        <Mail className="w-4 h-4 ah-accent" strokeWidth={2.5} />
        <p className="font-bold text-sm">{c.headline || "Make an enquiry"}</p>
      </div>
      {c.subtext && <p className="text-xs ah-muted text-center">{c.subtext}</p>}
      <input
        type="text" placeholder="Your name" required
        value={state.name} onChange={(e) => setState({ ...state, name: e.target.value })}
        className="w-full border-2 px-2 py-1 text-sm bg-transparent" style={{ borderColor: "var(--ah-fg)" }}
        data-testid={`enquiry-input-name-${block.block_id}`}
      />
      <input
        type="email" placeholder="you@example.com" required
        value={state.email} onChange={(e) => setState({ ...state, email: e.target.value })}
        className="w-full border-2 px-2 py-1 text-sm bg-transparent" style={{ borderColor: "var(--ah-fg)" }}
        data-testid={`enquiry-input-email-${block.block_id}`}
      />
      {(c.show_phone !== false) && (
        <>
          <PhoneInput
            value={state.phone}
            onChange={(v) => setState({ ...state, phone: v })}
            placeholder="10-digit mobile number (optional)"
            data-testid={`enquiry-input-phone-${block.block_id}`}
            className="w-full"
          />
          <MarketingConsentCheckbox
            checked={state.marketing_consent}
            onChange={(v) => setState({ ...state, marketing_consent: v })}
            testid={`enquiry-consent-${block.block_id}`}
            compact
          />
        </>
      )}
      <input
        type="text" placeholder={c.subject_placeholder || "Subject"}
        value={state.subject} onChange={(e) => setState({ ...state, subject: e.target.value })}
        className="w-full border-2 px-2 py-1 text-sm bg-transparent" style={{ borderColor: "var(--ah-fg)" }}
        data-testid={`enquiry-input-subject-${block.block_id}`}
      />
      <textarea
        placeholder={c.message_placeholder || "Tell us what you need…"} rows={3} required
        value={state.message} onChange={(e) => setState({ ...state, message: e.target.value })}
        className="w-full border-2 px-2 py-1 text-sm bg-transparent" style={{ borderColor: "var(--ah-fg)" }}
        data-testid={`enquiry-input-message-${block.block_id}`}
      />
      <button
        type="submit"
        disabled={status === "submitting" || editable}
        className="w-full border-2 font-bold text-sm py-1.5 flex items-center justify-center gap-2 disabled:opacity-50"
        style={{ borderColor: "var(--ah-fg)", background: "var(--ah-cta-bg)", color: "var(--ah-cta-fg)" }}
        data-testid={`enquiry-submit-${block.block_id}`}
      >
        <Send className="w-3 h-3" /> {c.button_label || "Send enquiry"}
      </button>
      {status === "ok" && <p className="text-xs font-bold text-center ah-accent" data-testid={`enquiry-thanks-${block.block_id}`}>Thanks! We&apos;ll reply shortly.</p>}
      {status === "err" && <p className="text-xs font-bold text-center" style={{ color: "#dc2626" }}>Something went wrong. Please try again.</p>}
    </form>
  );
}

export function LocationBlock({ block, editable, onClick }) {
  const c = block.config || {};
  const lat = Number(c.lat);
  const lng = Number(c.lng);
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  const [MapModules, setMapModules] = useState(null);
  // v149.19 — A map inside a scrolling page is a touch trap: Leaflet's
  // drag handler (and, for the Google provider, the embed iframe) claims
  // every `touchmove` that starts inside the map, so a finger swipe pans
  // the map instead of scrolling the Bio Page. On a 220px-tall block
  // that is a dead band the reader has to swipe around, which is what
  // "scrolling gets stuck / I have to scroll slowly" actually was.
  //
  // Fix: on touch pointers the map starts INERT behind a transparent
  // shield, so swipes scroll the page as if the map were an image. One
  // deliberate tap activates it and hands full pan/zoom back. Mouse
  // users (fine pointer) never see the shield — desktop is unchanged.
  const [mapActive, setMapActive] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);
  const mapWrapRef = useRef(null);
  const [isTouch] = useState(() => {
    try { return window.matchMedia("(pointer: coarse)").matches; }
    catch { return false; }
  });
  // v146.22 — Provider config so the public block matches whatever the
  // Super Admin picked in Integrations. Google renders via lightweight
  // Embed iframe (no SDK load) since the block is read-only.
  const [mapsCfg, setMapsCfg] = useState(null);

  // Dynamic import so react-leaflet only loads when a location block renders.
  useEffect(() => {
    let alive = true;
    // Fetch the maps provider first — for Google we skip Leaflet entirely.
    fetch("/api/public/maps-config").then((r) => r.ok ? r.json() : { provider: "osm" })
      .catch(() => ({ provider: "osm" }))
      .then((cfg) => {
        if (!alive) return;
        setMapsCfg(cfg);
        if (cfg.provider === "google") return; // no Leaflet needed
        Promise.all([
          import("react-leaflet"),
          import("leaflet"),
        ]).then(([rl, L]) => {
          if (!alive) return;
          delete L.default.Icon.Default.prototype._getIconUrl;
          L.default.Icon.Default.mergeOptions({
            iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
            iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
            shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
          });
          setMapModules({ ...rl, L: L.default });
        });
      });
    return () => { alive = false; };
  }, []);

  // react-leaflet applies MapContainer props once, at mount, so flipping
  // `dragging` later never reaches the live map. Drive the handler on the
  // instance instead — this is what actually hands panning back after the
  // viewer taps the shield.
  useEffect(() => {
    if (!mapInstance || !mapInstance.dragging) return;
    if (!isTouch || mapActive) mapInstance.dragging.enable();
    else mapInstance.dragging.disable();
  }, [mapInstance, mapActive, isTouch]);

  // v149.21 — Activation is scoped to the time the map is on screen. Once it
  // scrolls out of the viewport the shield comes back, so the map can never
  // hold on to the page's vertical gestures after the reader has moved on —
  // and scrolling back to it starts from the same inert state as a fresh
  // page load. Touch only: a mouse never had a shield to restore.
  useEffect(() => {
    if (!isTouch || !mapActive) return;
    const el = mapWrapRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const en of entries) {
          if (!en.isIntersecting) setMapActive(false);
        }
      },
      // threshold 0 -> fires when the last pixel leaves, so deactivating
      // never interrupts a pan the reader is still watching.
      { threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [isTouch, mapActive]);

  if (!hasCoords) {
    return (
      <div className="ah-block text-center" data-testid={`block-location-empty-${block.block_id}`}>
        <MapPin className="w-5 h-5 mx-auto ah-muted" />
        <p className="text-xs ah-muted mt-1">Location not set — open editor to pick a spot on the map.</p>
      </div>
    );
  }

  // v147.5 — Display mode: "map" (map only), "button" (directions
  // button only, DEFAULT), "both" (map + button). Editor-mode forces
  // "both" so creators can still see + edit everything. Default is
  // now "button" — visitors get a one-tap "Get directions" CTA and
  // the map is only shown when the creator explicitly opts in.
  const displayMode = editable
    ? "both"
    : (c.display_mode === "map" || c.display_mode === "both"
        ? c.display_mode
        : "button");
  const showMap = displayMode !== "button";
  const showButton = displayMode !== "map";

  if (!MapModules && mapsCfg?.provider !== "google" && showMap) {
    return (
      <div className="ah-block text-center" style={{ height: 180 }} data-testid={`block-location-loading-${block.block_id}`}>
        <p className="text-xs ah-muted pt-16">Loading map…</p>
      </div>
    );
  }

  const useGoogle = mapsCfg?.provider === "google" && !!mapsCfg?.browser_key;
  const zoom = Number(c.zoom) || 15;
  const height = Number(c.height) || 220;
  // v96 — open Google Maps directions using the user's current location as
  // the origin. Passing no `saddr` lets Google fall back to "My location"
  // (the browser/app's current position). Works cross-platform: on iOS/
  // Android this opens the native Maps app; on the web it opens
  // maps.google.com.
  const directionsUrl =
    `https://www.google.com/maps/dir/?api=1` +
    `&destination=${lat},${lng}` +
    `&travelmode=driving`;

  const handleDirectionsClick = () => {
    // Fire click analytics for the "Get directions" CTA on the parent tracker.
    if (onClick) {
      try { onClick({ preventDefault: () => {} }, directionsUrl); } catch { /* ignore */ }
    }
    // Best-effort: ask for geolocation so Google Maps opens with an
    // exact origin when the user's browser has granted permission.
    // If permission is denied we still open the URL (Google will use
    // its own IP-based approximation).
    if (isGeolocationSupported()) {
      getCurrentPositionSafe(
        (pos) => {
          const url =
            `https://www.google.com/maps/dir/?api=1` +
            `&origin=${pos.coords.latitude},${pos.coords.longitude}` +
            `&destination=${lat},${lng}` +
            `&travelmode=driving`;
          openExternalUrl(url);
        },
        () => openExternalUrl(directionsUrl),
        { timeout: 4000 },
      );
    } else {
      openExternalUrl(directionsUrl);
    }
  };

  return (
    <div className="ah-block" data-testid={`block-location-${block.block_id}`}>
      {c.headline && <p className="font-bold text-sm text-center mb-2">{c.headline}</p>}
      {showMap && (
        <div
          style={{
            height,
            borderRadius: "var(--ah-block-radius)",
            overflow: "hidden",
            // v147.5 — Contain leaflet's internal z-index stack. Leaflet
            // uses z-index 400+ for tiles and 600+ for markers/popups,
            // which was lifting the map above sticky headers / modals
            // on iOS. `isolation: isolate` opens a new stacking context
            // so those internal values never bleed outside this box.
            position: "relative",
            zIndex: 0,
            isolation: "isolate",
          }}
          ref={mapWrapRef}
        >
          {useGoogle ? (
            <iframe
              src={`https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(mapsCfg.browser_key)}&q=${lat},${lng}&zoom=${zoom}`}
              title="Location map"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid={`location-gmap-${block.block_id}`}
            />
          ) : (
            (() => {
              const { MapContainer, TileLayer, Marker, Popup } = MapModules;
              return (
                <MapContainer
                  center={[lat, lng]}
                  zoom={zoom}
                  style={{ height: "100%", width: "100%" }}
                  scrollWheelZoom={false}
                  // v149.19 — see `mapActive` above. Off until tapped on
                  // touch; always on for mouse users.
                  dragging={!isTouch || mapActive}
                  ref={setMapInstance}
                  data-testid={`location-map-${block.block_id}`}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[lat, lng]}>
                    {c.address && <Popup>{c.address}</Popup>}
                  </Marker>
                </MapContainer>
              );
            })()
          )}

          {/* v149.19 — Scroll shield. Transparent, so the map stays fully
              visible; it simply receives the touch instead of the map, and
              because it never calls preventDefault the page scrolls
              normally. Tapping it activates the map for real interaction.
              Rendered only for touch pointers, and only while inert. */}
          {isTouch && !mapActive && (
            <button
              type="button"
              onClick={() => setMapActive(true)}
              aria-label="Activate map"
              // Leaflet's own panes run z-index 400 (tiles) to 700
              // (popups) and they share this wrapper's stacking context,
              // so the shield has to sit above them or a marker swallows
              // the activating tap. 900 clears the panes while staying
              // below Leaflet's zoom controls (1000), which therefore
              // keep working without activating the map.
              style={{ zIndex: 900 }}
              className="absolute inset-0 w-full h-full bg-transparent flex items-end justify-center pb-2"
              data-testid={`location-map-shield-${block.block_id}`}
            >
              <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-black/55 text-white pointer-events-none">
                Tap to interact
              </span>
            </button>
          )}
        </div>
      )}
      {c.address && (
        <p className="text-xs ah-muted text-center mt-2 flex items-center gap-1 justify-center" data-testid={`block-location-address-${block.block_id}`}>
          <MapPin className="w-3 h-3 flex-shrink-0" />
          <span>{c.address}</span>
        </p>
      )}
      {!editable && showButton && (
        <button
          type="button"
          onClick={handleDirectionsClick}
          className="mt-2 px-4 py-3 min-h-[48px] font-semibold text-[15px] flex items-center justify-center gap-2 w-full rounded-xl transition-transform active:scale-[0.99]"
          style={{
            background: "var(--ah-cta-bg)",
            color: "var(--ah-cta-fg)",
            border: "1px solid var(--ah-cta-bg)",
            boxShadow: "var(--ux-shadow-1, 0 1px 2px rgba(22,22,22,0.06))",
          }}
          data-testid={`block-location-directions-${block.block_id}`}
        >
          <Navigation className="w-4 h-4" strokeWidth={2.2} /> {c.button_label || "Get directions"}
        </button>
      )}
    </div>
  );
}



// ---------------------------------------------------------------------------
// PaidCouponBlock — embeds a paid-coupon-marketplace campaign as a card on an
// Action Page / Bio Hub. Clicking navigates to `/coupon/:campaign_id` (the
// public unlock flow). Business details stay hidden until purchase.
// v104 — Now renders a rich pre-unlock teaser (offer summary, teaser
// description, expiry hint) so the block itself sells the coupon instead of
// showing a bland 1-liner.
// ---------------------------------------------------------------------------
